
import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useAuthActions } from '@/hooks/useAuthActions';
import { AuthContextType } from '@/types/auth.types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { signInWithEmail: signIn, signUp, signOut: logout, resetPassword } = useAuthActions();

  useEffect(() => {
    console.log('Initializing AuthContext...');
    let authSubscription: { unsubscribe: () => void } | null = null;
    
    const initializeAuth = async () => {
      try {
        // Set up auth subscription first so we don't miss any auth events
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, currentSession) => {
          console.log('Auth state changed:', event, 'session:', currentSession ? 'present' : 'null');
          
          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            console.log('User signed in or token refreshed');
          }
          
          if (currentSession && currentSession.user) {
            console.log('Updating session in state from auth change, user ID:', currentSession.user.id);
            setSession(currentSession);
            setUser(currentSession.user);
          } else {
            console.log('Clearing session in state from auth change');
            setSession(null);
            setUser(null);
          }
          
          // Don't set loading to false here, wait for getSession to complete
        });
        
        authSubscription = subscription;

        // Then check for active session
        console.log('Checking for active session...');
        const { data: { session: activeSession }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Error getting session:', error);
          setLoading(false);
          return;
        }

        if (activeSession) {
          console.log('Active session found during initialization, user ID:', activeSession.user?.id);
          setSession(activeSession);
          setUser(activeSession.user);
        } else {
          console.log('No active session found during initialization');
        }
      } catch (error) {
        console.error('Error checking auth session:', error);
      } finally {
        // Only set loading to false after both session check and auth subscription are set up
        console.log('Auth initialization complete, setting loading to false');
        setLoading(false);
      }
    };

    initializeAuth();

    // Clean up the subscription when the component unmounts
    return () => {
      console.log('Cleaning up auth subscription');
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
    };
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    console.log('Signing in with email...');
    return await signIn(email, password);
  };

  const signOut = async () => {
    console.log('Signing out...');
    return await logout();
  };

  const value = {
    user,
    session,
    signInWithEmail,
    signUp,
    signOut,
    resetPassword,
    loading,
  };

  console.log('AuthContext current state:', { 
    user: user ? 'present' : 'null', 
    loading,
    sessionExpires: session?.expires_at ? new Date(session.expires_at * 1000).toLocaleString() : 'N/A'
  });
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
